import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
import { alpha, useTheme, experimentalStyled as styled } from '@mui/material/styles';
import { Box, List, ListItem, Collapse, ListItemText, ListItemIcon } from '@mui/material';

const ListItemStyle = styled((props) => <ListItem button disableGutters {...props} />)(({ theme }) => ({
  ...theme.typography.body2,
  height: 48,
  position: 'relative',
  textTransform: 'capitalize',
  paddingLeft: theme.spacing(5),
  paddingRight: theme.spacing(2.5),
  color: theme.palette.text.secondary,
  '&:before': {
    top: 0,
    right: 0,
    width: 3,
    bottom: 0,
    content: "''",
    display: 'none',
    position: 'absolute',
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    backgroundColor: theme.palette.primary.main
  }
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func
};

function NavItem({ item, active }) {
  const theme = useTheme();
  const { title, path, icon, info, children, defaultOpen } = item;
  // A group is "active" when the page you are on is one of its children, not
  // only when it has a landing page of its own — most of card 208's groups
  // (Upkeep, Records, People) are pure containers with no page behind them.
  const isActiveChild = (children || []).some((child) => active(child.path));
  const isActiveRoot = active(path) || isActiveChild;
  const [open, setOpen] = useState(Boolean(isActiveRoot || defaultOpen));

  // useState only reads its argument on mount, and the sidebar stays mounted
  // across navigation — without this, arriving at a child route (a dashboard
  // link, a bookmark, the back button) left its group collapsed and the
  // homeowner lost their place. Expanding only: a group the user closed by hand
  // stays closed while they are elsewhere.
  useEffect(() => {
    if (isActiveRoot) setOpen(true);
  }, [isActiveRoot]);

  const handleOpen = () => {
    setOpen((prev) => !prev);
  };

  const activeRootStyle = {
    color: 'primary.main',
    fontWeight: 'fontWeightMedium',
    bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
    '&:before': { display: 'block' }
  };

  const activeSubStyle = {
    color: 'text.primary',
    fontWeight: 'fontWeightMedium'
  };

  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
          sx={{
            ...(isActiveRoot && activeRootStyle)
          }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
          <ListItemText disableTypography primary={title} />
          {info && info}
          <Box
            component={Icon}
            icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
            sx={{ width: 16, height: 16, ml: 1 }}
          />
        </ListItemStyle>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((item) => {
              const { title, path } = item;
              const isActiveSub = active(path);

              return (
                <ListItemStyle
                  key={title}
                  component={RouterLink}
                  to={path}
                  sx={{
                    ...(isActiveSub && activeSubStyle)
                  }}
                >
                  <ListItemIconStyle>
                    <Box
                      component="span"
                      sx={{
                        width: 4,
                        height: 4,
                        display: 'flex',
                        borderRadius: '50%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'text.disabled',
                        transition: (theme) => theme.transitions.create('transform'),
                        ...(isActiveSub && {
                          transform: 'scale(2)',
                          bgcolor: 'primary.main'
                        })
                      }}
                    />
                  </ListItemIconStyle>
                  <ListItemText disableTypography primary={title} />
                </ListItemStyle>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <ListItemStyle
      component={RouterLink}
      to={path}
      sx={{
        ...(isActiveRoot && activeRootStyle)
      }}
    >
      <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
      <ListItemText disableTypography primary={title} />
      {info && info}
    </ListItemStyle>
  );
}

NavSection.propTypes = {
  navConfig: PropTypes.array,
  user: PropTypes.object
};

export default function NavSection({ navConfig, user, ...other }) {
  const { pathname, search } = useLocation();

  // The seven category links are all the same route, told apart only by
  // ?category=. matchPath escapes the "?" as a literal, so a path carrying a
  // query string matched nothing and those items never highlighted at all —
  // invisible while they sat flat in one list, obvious once card 208 grouped
  // them. Match the pathname, then require the query to agree.
  const match = (path) => {
    if (!path) return false;
    const [pathOnly, searchOnly] = path.split('?');
    if (!matchPath({ path: pathOnly, end: false }, pathname)) return false;
    return searchOnly ? search === `?${searchOnly}` : true;
  };
  return (
    <Box {...other}>
      {navConfig.map((list) => {
        const { subheader, items } = list;
        return (
          <List key={subheader} disablePadding>
            {items.map((item) => {
              const nav = shouldShowItem(item, user) ? <NavItem key={item.title} item={item} active={match} /> : null;
              return nav;
            })}
          </List>
        );
      })}
    </Box>
  );
}

// The admin group is gated by its title, so this has to survive card 208
// re-casing the menu labels — a title compared case-sensitively would have
// silently exposed the admin menu to every homeowner.
const shouldShowItem = (item, user) => String(item.title).toLowerCase() !== 'admin' || user.role === 'Admin';
